import type { CalculationFunction, CalculationFunctionParams, CalculationKey, FormFieldValues } from '@/ts/types/calculation-config.type.ts'
import {
  calculateAbsoluteSavings,
  calculateCareInsurance,
  calculateExpectedUsedPurchasePrice,
  calculateHealthInsurance,
  calculateInsuranceCosts,
  calculateLeasingCalculationBasis,
  calculateLeasingNetChargeOverDuration,
  calculateLeasingRate,
  calculateMonetaryBenefit,
  calculateMonthlyNetBurden,
  calculateNetPayout,
  calculatePensionInsurance,
  calculateRelativeSavings,
  calculateSocialInsuranceContribution,
  calculateTaxes,
  calculateTotalCostPrivateBuy,
  calculateTotalCostsLeasing,
  calculateUnemploymentInsurance
} from '@/helpers/calculation-formula.helpers.ts'
import type { CareInsuranceConfig, FederalStateConfig, InsuranceBonus } from '@/ts/types/config.types'
import { toNumber } from '@/helpers/parser.ts'

export function getCareInsuranceEmployeeValue(childrenCount: number, employeeValues: { [p: number]: number }) {
  if (employeeValues[childrenCount]) return employeeValues[childrenCount]
  const highestChildCount = Object.keys(employeeValues).map((employeeValue) => parseFloat(employeeValue))
  return employeeValues[Math.max(...highestChildCount)]
}

export function getEmployeeWithoutChildrenValue(
  childAllowance: FormFieldValues | undefined,
  careInsuranceConfig: CareInsuranceConfig
): number {
  // @TODO: check why 'Keine Kinder' and not just 0?
  return childAllowance === 'Keine Kinder' ? careInsuranceConfig.employee_without_children : 0
}

export function getChildAllowanceCount(childAllowance: FormFieldValues | undefined) {
  // @TODO: check why 'Keine Kinder' and not just 0?
  return childAllowance !== 'Keine Kinder' ? toNumber(childAllowance as number) : 0
}

export function createSocialInsuranceCalculationFn(
  calculationBasisField: 'private_buy.calculationBasis' | 'leasing.calculationBasis'
): (params: CalculationFunctionParams) => number | null {
  return ({ fieldValues, calculationResults, config }: CalculationFunctionParams) => {
    const { federal_state, child_allowance, health_insurance_additional_contribution, health_insurance, pension_insurance } = fieldValues
    const calculationBasis = calculationResults[calculationBasisField]
    const { federal_states, social_insurance } = config ?? {}
    const federalState = federal_states?.find((federalState) => federalState.name === federal_state)
    const isPrivate: boolean = !health_insurance
    const healthInsuranceAdditionalContribution = health_insurance ? health_insurance_additional_contribution : 0

    const careInsuranceConfig = federalState!.care_insurance

    const {
      pension_insurance: pensionInsuranceConfig,
      unemployment_insurance: unemploymentInsuranceConfig,
      health_insurance: healthInsuranceConfig
    } = social_insurance!

    const childAllowanceCount = getChildAllowanceCount(child_allowance)
    const employeeWithoutChildren = getEmployeeWithoutChildrenValue(child_allowance, careInsuranceConfig)
    const careInsuranceEmployeeValue: number = getCareInsuranceEmployeeValue(childAllowanceCount, careInsuranceConfig.employee_values)

    const resultPensionInsurance = calculatePensionInsurance(
      calculationBasis as number,
      federalState!.pension_insurance.contribution_assessment_limit,
      pensionInsuranceConfig.employee_value,
      pension_insurance as boolean
    )
    const resultUnemploymentInsurance = calculateUnemploymentInsurance(
      calculationBasis as number,
      federalState!.unemployment_insurance.contribution_assessment_limit,
      unemploymentInsuranceConfig.employee_value,
      pension_insurance as boolean
    )
    const resultCareInsurance = calculateCareInsurance(
      calculationBasis as number,
      careInsuranceEmployeeValue,
      employeeWithoutChildren,
      careInsuranceConfig.contribution_assessment_limit,
      health_insurance as boolean
    )
    const resultHealthInsurance = calculateHealthInsurance(
      calculationBasis as number,
      healthInsuranceConfig.employee_value,
      toNumber(healthInsuranceAdditionalContribution as string | number),
      healthInsuranceConfig.contribution_assessment_limit,
      isPrivate
    )

    return calculateSocialInsuranceContribution(
      resultCareInsurance,
      resultUnemploymentInsurance,
      resultHealthInsurance,
      resultPensionInsurance
    )
  }
}

export function createTaxCalculationFn(
  type: 'leasing.calculationBasis' | 'private_buy.calculationBasis'
): (params: CalculationFunctionParams) => Promise<number | null> {
  return async ({ fieldValues, calculationResults, config }: CalculationFunctionParams): Promise<number | null> => {
    const {
      federal_state,
      tax_class,
      church_tax: isLiableToChurchTax,
      child_allowance,
      pension_insurance: isLiableToStatutoryPensionInsurance,
      health_insurance: isLiableToStatutoryHealthInsurance,
      health_insurance_additional_contribution
    } = fieldValues
    const calculationBasis = calculationResults[type]
    const healthInsuranceAdditionalContribution = isLiableToStatutoryHealthInsurance ? health_insurance_additional_contribution : 0
    const { federal_states, taxes } = config ?? {}
    const selectedFederalState = federal_states?.find((federalState) => federalState.name === federal_state)
    const childAllowanceValue = child_allowance && child_allowance !== 'Keine Kinder' ? toNumber(child_allowance as number) : 0

    return await calculateTaxes(
      calculationBasis! as number,
      selectedFederalState! as FederalStateConfig,
      isLiableToChurchTax! as boolean,
      childAllowanceValue as number,
      taxes!.child_allowance,
      toNumber(tax_class as number),
      isLiableToStatutoryPensionInsurance as boolean,
      isLiableToStatutoryPensionInsurance as boolean,
      isLiableToStatutoryHealthInsurance as boolean,
      toNumber(healthInsuranceAdditionalContribution as string | number)
    )
  }
}

export function getInsuranceBonus(
  insuranceBonusList: InsuranceBonus[],
  price: number,
  assumptionOfInsuranceCosts: boolean = false
): number {
  if (!insuranceBonusList.length || assumptionOfInsuranceCosts) return 0
  let result: InsuranceBonus | null = null
  for (const insuranceBonus of insuranceBonusList) {
    result = insuranceBonus.price_class >= price && (!result || insuranceBonus.price_class <= result.price_class) ? insuranceBonus : result
  }
  return result?.bonus ?? 0
}

const calculations: { [Key in CalculationKey]: CalculationFunction } = {
  monthlyNetBurden: ({ calculationResults }) =>
    calculateMonthlyNetBurden(calculationResults['private_buy.netPayout'] as number, calculationResults['leasing.netPayout'] as number),

  absoluteSavings: ({ calculationResults }) =>
    calculateAbsoluteSavings(calculationResults['private_buy.totalCost'] as number, calculationResults['leasing.totalCost'] as number),

  relativeSavings: ({ calculationResults }) =>
    calculateRelativeSavings(calculationResults.absoluteSavings as number, calculationResults['private_buy.totalCost'] as number),

  'private_buy.grossSalary': ({ fieldValues }) => toNumber(fieldValues.monthly_salary as number),

  'private_buy.employerSubsidy': () => null,

  'private_buy.leasingRate': () => null,

  'private_buy.monetaryAdvantage': () => null,

  'private_buy.calculationBasis': ({ fieldValues }) => toNumber(fieldValues.monthly_salary as number),

  'private_buy.tax': (params) => createTaxCalculationFn('private_buy.calculationBasis')(params),

  'private_buy.socialInsurance': (params) => createSocialInsuranceCalculationFn('private_buy.calculationBasis')(params),

  'private_buy.netPayout': ({ calculationResults }) =>
    calculateNetPayout(
      calculationResults['private_buy.calculationBasis'] as number,
      calculationResults['private_buy.tax'] as number,
      calculationResults['private_buy.socialInsurance'] as number
    ),

  'private_buy.netBurdenDuration': () => null,

  'private_buy.purchasePrice': ({ fieldValues }) => toNumber(fieldValues.purchase_price as number),

  'private_buy.insuranceCost': ({ fieldValues, config }) =>
    calculateInsuranceCosts(
      getInsuranceBonus(config?.insurance_bonus as InsuranceBonus[], toNumber(fieldValues.purchase_price as number)),
      config?.factors.duration as number
    ),

  'private_buy.expectedUsedPurchasePrice': () => null,

  'private_buy.totalCost': ({ fieldValues, calculationResults }) =>
    calculateTotalCostPrivateBuy(toNumber(fieldValues.purchase_price as number), calculationResults['private_buy.insuranceCost'] as number),

  'leasing.grossSalary': ({ fieldValues }) => toNumber(fieldValues.monthly_salary as number),

  'leasing.employerSubsidy': ({ fieldValues }) => toNumber(fieldValues.employer_subsidy as string),

  'leasing.leasingRate': ({ fieldValues, config }) =>
    calculateLeasingRate(
      toNumber(fieldValues.purchase_price as number),
      config?.factors.leasing_factor as number,
      fieldValues.employer_tax_deduction as boolean,
      config?.taxes.vat
    ),

  'leasing.monetaryAdvantage': ({ fieldValues }) => calculateMonetaryBenefit(toNumber(fieldValues.uvp_price as number)),

  'leasing.calculationBasis': ({ fieldValues, calculationResults }) =>
    calculateLeasingCalculationBasis(
      toNumber(fieldValues.monthly_salary as number),
      toNumber(fieldValues.employer_subsidy as string),
      fieldValues.assumption_of_insurance_costs as boolean,
      calculationResults['leasing.leasingRate'] as number,
      calculationResults['leasing.insuranceBonus'] as number,
      calculationResults['leasing.monetaryAdvantage'] as number
    ),

  'leasing.tax': (params) => createTaxCalculationFn('leasing.calculationBasis')(params),

  'leasing.socialInsurance': (params) => createSocialInsuranceCalculationFn('leasing.calculationBasis')(params),

  'leasing.netPayout': ({ calculationResults }) =>
    calculateNetPayout(
      calculationResults['leasing.calculationBasis'] as number,
      calculationResults['leasing.tax'] as number,
      calculationResults['leasing.socialInsurance'] as number,
      calculationResults['leasing.monetaryAdvantage'] as number
    ),

  'leasing.netBurdenDuration': ({ calculationResults, config }) =>
    calculateLeasingNetChargeOverDuration(calculationResults.monthlyNetBurden as number, config?.factors.duration as number),

  'leasing.purchasePrice': () => 'inkl.',

  'leasing.insuranceCost': () => 'inkl.',

  'leasing.insuranceBonus': ({ fieldValues, config }) =>
    getInsuranceBonus(
      config?.insurance_bonus as InsuranceBonus[],
      toNumber(fieldValues.purchase_price as number),
      fieldValues.assumption_of_insurance_costs as boolean
    ),

  'leasing.expectedUsedPurchasePrice': ({ fieldValues, config }) =>
    calculateExpectedUsedPurchasePrice(
      toNumber(fieldValues.uvp_price as number),
      config?.factors.expected_used_purchase_price_factor as number
    ),

  'leasing.totalCost': ({ calculationResults }) =>
    calculateTotalCostsLeasing(
      calculationResults['leasing.netBurdenDuration'] as number,
      calculationResults['leasing.expectedUsedPurchasePrice'] as number
    )
}

export function executeCalculationFnOfCalculationKey(calculationKey: CalculationKey, params: CalculationFunctionParams) {
  return calculations[calculationKey](params)
}
